<template>
  <div>
    <v-card :class="showPhotoRequired ? 'error-border' : ''">
      <v-card-title> Add a Profile Photo <small class="ml-2 error--text">*</small></v-card-title>
      <v-card-text>
        <v-row align="stretch">
          <v-col cols="12" class="pt-0" align-self="center">
            <div class="image-container" :class="{ 'has-image': personalDetailModel && personalDetailModel.PhotoUrl }">
              <v-img
                v-if="personalDetailModel && personalDetailModel.PhotoUrl && !isHiringmanager"
                class="profile-image"
                :src="personalDetailModel.PhotoUrl"
                aspect-ratio="1"
                @click="imageDialog = true"
              />
            </div>
            <v-col md="12" class="upload-button text-center">
              <v-btn v-if="!isHiringmanager" class="secondary_btn pt-0" @click="uploadImage = !uploadImage">Upload a Photo.</v-btn>
              <v-btn v-if="!isHiringmanager" icon @click="showPhotoRequirements = true">
                <v-icon>mdi-information-variant</v-icon>
              </v-btn>
            </v-col>
          </v-col>
        </v-row>
        <v-row class="mt-0">
          <v-col cols="12" align="center" v-if="personalDetail" class="pt-0">
            <div class="user-name">
              <span>{{ personalDetail.FirstName }}&nbsp;</span>
              <span>{{ personalDetail.LastName }}</span>
            </div>
            <div class="nick-name" v-if="personalDetail.NickName">( {{ personalDetail.NickName }} )</div>
            <div class="email">{{ personalDetail.Email }}</div>
            <div class="title" v-if="personalDetail.Title">{{ personalDetail.Title }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-dialog v-model="uploadImage" persistent max-width="600px">
      <v-card>
        <v-card-text>
          <v-col align-self="center">
            <v-row justify="center">
              <v-col md="12" sm="12" cols="12" align="center">
                <input ref="input" type="file" name="image" accept="image/*" @change="setImage" />
                <VuePictureCropper
                  :boxStyle="{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    margin: 'auto',
                  }"
                  :img="imageData"
                  :options="{
                    viewMode: 1,
                    dragMode: 'move',
                    aspectRatio: 1,
                    cropBoxResizable: false,
                  }"
                  :presetMode="{
                    mode: 'round',
                    width: 400,
                    height: 400,
                  }"
                />

                <vue-cropper ref="cropper" :src="imageData" preview=".preview" />
              </v-col>
              <v-col md="12" sm="12" cols="12">
                <v-row>
                  <v-col align="center">
                    <v-btn class="ml-1 mr-1" @click="zoom(0.1)" icon="mdi-magnify-plus-outline"></v-btn>
                    <v-btn class="ml-1 mr-1" @click="zoom(-0.1)" icon="mdi-magnify-minus-outline"></v-btn>
                    <v-btn class="ml-1 mr-1" @click="rotate(90)" icon="mdi-rotate-right"></v-btn>
                    <v-btn class="ml-1 mr-1" @click="rotate(-90)" icon="mdi-rotate-left"></v-btn>
                    <v-btn class="ml-1 mr-1" @click="flipH()" icon="mdi-flip-horizontal"></v-btn>
                    <v-btn class="ml-1 mr-1" @click="flipV()" icon="mdi-flip-vertical"></v-btn>
                    <v-btn class="ml-1 mr-1" @click="reset()" icon="mdi-refresh"></v-btn>
                  </v-col>
                </v-row>
                <v-row justify="center">
                  <v-btn @click="showFileChooser"> Choose an image </v-btn>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="mt-7" v-if="fileMessage && fileMessage.length > 0">
              <p v-for="message in fileMessage" :key="message" class="mb-0 error--text">
                {{ message }}
              </p>
            </v-row>
          </v-col>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="!isHiringmanager" color="secondary" @click="setImages">
            <v-icon>mdi-check</v-icon>
            Accept</v-btn
          >
          <v-btn v-if="!isHiringmanager" color="primary" @click="uploadImage = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="showPhotoRequirements" v-model="showPhotoRequirements" max-width="500" @keydown.esc="showPhotoRequirements = false">
      <InformationDialog :title="informationDialogTitle" :messages="photoReqirements" @cancel="showPhotoRequirements = false"></InformationDialog>
    </v-dialog>
  </div>
</template>
<script lang="ts">
import InformationDialog from "../InformationDialog.vue";
import { defineComponent } from "vue";
import { PersonalDetailModel } from "../../models/PersonalDetailModel";
import VuePictureCropper, { cropper } from "vue-picture-cropper";

export default defineComponent({
  props: ["personalDetail", "isFileValid", "isHiringmanager"],
  components: {
    InformationDialog,
    VuePictureCropper,
  },
  data() {
    return {
      previews: {},
      base64: "",
      result: {
        dataURL: "",
        blobURL: "",
      },
      data: "",
      cropImg: "",
      imageDialog: false,
      validFile: false,
      fileMessage: [] as string[] | null,
      maxPhotoFileSize: 25000000,
      showPhotoRequired: false,
      fileTypes: ["image/png", "image/jpeg", "image/jpg"],
      photoReqirements: ["Format: JPG / PNG file", "Minimum resolution: 380x380px", "Maximum file size: 25 MB"],
      informationDialogTitle: "Photo requirements",
      personalDetailModel: null as PersonalDetailModel | null,
      imageData: "",
      uploadImage: false,
      showPhotoRequirements: false,
    };
  },
  watch: {
    isFileValid(newVal) {
      this.showPhotoRequired = !newVal;
    },
    async personalDetail(newVal) {
      this.personalDetailModel = { ...newVal };
      if (this.personalDetailModel?.PhotoUrl) {
        this.imageData = this.personalDetailModel.PhotoUrl;
        this.validFile = true;
      }
    },
  },
  async mounted() {
    this.personalDetailModel = { ...this.personalDetail };
    if (this.personalDetailModel?.PhotoUrl) {
      this.imageData = this.personalDetailModel.PhotoUrl;
      this.validFile = true;
    }
  },
  methods: {
    flipH() {
      var imgData = cropper?.getImageData();
      if (imgData?.scaleX) {
        cropper?.scaleX(imgData?.scaleX * -1);
      }
    },
    flipV() {
      var imgData = cropper?.getImageData();
      if (imgData?.scaleY) {
        cropper?.scaleY(imgData?.scaleY * -1);
      }
    },
    getfileinfo(data: any) {
      this.validFile = true;
      this.fileMessage = [];
      if (!data) {
        this.imageData = "";
        this.validFile = false;
        this.fileMessage?.push("Uploading Profile Photo is required!");
      } else {
        if (data.size > this.maxPhotoFileSize) {
          this.imageData = "";
          this.validFile = false;
          this.fileMessage?.push("Large file! you can't upload a file that is more than 25 MB");
        }
        if (!this.fileTypes.includes(data.type)) {
          this.imageData = "";
          this.validFile = false;
          this.fileMessage?.push("Wrong file type! allowed file types: jpeg/png");
        }
      }
      this.showPhotoRequired = !this.validFile;
    },
    async setImages() {
      if (!cropper) return;
      const base64 = cropper.getDataURL();
      const blob: Blob | null = await cropper.getBlob();
      if (!blob) {
        if (this.personalDetailModel?.PhotoUrl) this.uploadImage = false;
        else this.fileMessage?.push("No New file selected!");
        return;
      }

      const file = await cropper!.getFile();

      this.result.dataURL = base64;
      this.imageData = base64;
      if (!this.validFile) {
        this.getfileinfo(blob);
      }
      if (this.personalDetailModel) {
        this.personalDetailModel.PhotoUrl = this.imageData;
      }
      this.result.blobURL = URL.createObjectURL(blob);
      this.$emit("setImage", this.imageData);
      if (!this.fileMessage || this.fileMessage.length == 0) this.uploadImage = false;
    },
    reset() {
      cropper?.reset();
    },
    rotate(deg: number) {
      cropper?.rotate(deg);
    },
    setImage(e: any) {
      const file = e.target.files[0];
      if (file.type.indexOf("image/") === -1) {
        alert("Please select an image file");
        return;
      }
      if (typeof FileReader === "function") {
        const reader = new FileReader();

        reader.onload = (event: any) => {
          this.imageData = event.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        alert("Sorry, FileReader API not supported");
      }
    },
    showFileChooser() {
      if (this.$refs.input) {
        (this.$refs.input as any).click();
      }
    },
    zoom(percent: any) {
      cropper?.zoom(percent);
    },
  },
});
</script>

<style scoped>
body {
  font-family: Arial, Helvetica, sans-serif;
  width: 1024px;
  margin: 0 auto;
}

input[type="file"] {
  display: none;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 5px 0;
}

.header h2 {
  margin: 0;
}

.header a {
  text-decoration: none;
  color: black;
}

.content {
  display: flex;
  justify-content: space-between;
}

.cropper-area {
  width: 614px;
}

.actions {
  margin-top: 1rem;
}

.actions a {
  display: inline-block;
  padding: 5px 15px;
  background: #0062cc;
  color: white;
  text-decoration: none;
  border-radius: 3px;
  margin-right: 1rem;
  margin-bottom: 1rem;
}

textarea {
  width: 100%;
  height: 100px;
}

.preview-area {
  width: 307px;
}

.preview-area p {
  font-size: 1.25rem;
  margin: 0;
  margin-bottom: 1rem;
}

.preview-area p:last-of-type {
  margin-top: 1rem;
}

.preview {
  width: 100%;
  height: calc(372px * (9 / 16));
  overflow: hidden;
}

.crop-placeholder {
  width: 100%;
  height: 200px;
  background: #ccc;
}

.cropped-image img {
  max-width: 100%;
}
</style>
